import {Component, inject} from '@angular/core';
import {DsBoxModule, DsButtonModule, DsLoadingModule, DsMessageModule, DsModalModule} from '@bmw-ds/components';
import {TranslateModule} from '@ngx-translate/core';
import {ServiceCaseHolder} from '../../../../core/service-case/models/service-case-holder';
import {UserData} from '../../../../core/user/models/user-data';
import {UserMapper} from '../../../../core/user/models/user-mapper';
import {LanguageService} from '../../../../core/user/services/language.service';
import {UserService} from '../../../../core/user/services/user.service';
import {Locales} from '../../../../locales';

@Component({
  selector: 'app-customer-language-popup',
  templateUrl: './customer-language-popup.component.html',
  imports: [DsModalModule, DsBoxModule, DsMessageModule, DsButtonModule, DsLoadingModule, TranslateModule]
})
export class CustomerLanguagePopupComponent {
  private readonly userService: UserService = inject(UserService);
  private readonly languageService: LanguageService = inject(LanguageService);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);

  popupVisible: boolean = false;
  showSaveError: boolean = false;
  showLanguageWarning: boolean = false;
  showLoadingIndicator: boolean = false;

  user: UserData;
  customerLanguage: string;
  validatedCustomerLanguage: string;

  constructor() {
    const userService: UserService = this.userService;

    userService.user$.subscribe((user: UserData): UserData => this.user = user);

    this.serviceCaseHolder.activeServiceCaseChanged.subscribe((): void => this.onServiceCaseChanged());
  }

  confirm(): void {
    this.showSaveError = false;
    this.showLoadingIndicator = true;

    this.user.setLocale(this.validatedCustomerLanguage);

    this.userService.setUserSettings(UserMapper.toUserSettings(this.user))
      .then((): Promise<void> => this.languageService.use(this.validatedCustomerLanguage)
        .then((): void => this.close()))
      .catch((): void => {
        this.showLoadingIndicator = false;
        this.showSaveError = true;
      })
      .finally(() => this.userService.updateUser(this.user));
  }

  close(): void {
    this.showSaveError = false;
    this.popupVisible = false;
    this.showLoadingIndicator = false;
  }

  private onServiceCaseChanged(): void {
    this.customerLanguage = this.serviceCaseHolder.getActiveCase()?.getCustomerLanguage();
    this.validatedCustomerLanguage = this.validateCustomerLanguage(this.customerLanguage);
    if (this.customerLanguage && this.user?.getLocale() !== this.validatedCustomerLanguage) {
      // System language does not match validated customer language
      this.popupVisible = true;
      this.showLanguageWarning = !Locales.getLocaleIds().includes(this.customerLanguage);
    }
  }

  private validateCustomerLanguage(language: string): string {
    if (Locales.getLocaleIds().includes(language)) {
      return language;
    } else {
      const fallback: string = Locales.getLocaleIds()
        .find((locale: string): boolean => locale.substring(0, 2) === language?.substring(0, 2));
      return fallback ?? 'en-US';
    }
  }
}
